import { ORDER_CONFIRM_URL, apiPostCall } from '@/api/apiDispatcher';
import { Suspense, lazy, startTransition, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Infobox from '@/components/_common/Infobox';
const OrderSummary = lazy(() => import('@/components/_common/OrderSummary'));
import { appDataContext } from '@/context';
import { getLangFromPath } from '@/utils/languageUtils';
import { setUserToken } from '@/api/createTokenProvider';
import styled from 'styled-components';
import { useLangStore } from '@/store/lang-store';

// import MazoutLoader from "@/components/_common/MazoutLoader";

const ConfirmOrder = () => {
  const dataContext = useContext(appDataContext);
  const [order, setOrder] = useState(null);
  const location = useLocation();
  const urlArray = location.pathname.split('/');
  const OrderKey = urlArray[urlArray.length - 1];
  const [errMsg, setErrMsg] = useState('');
  const [pendingMsg, setPendingrMsg] = useState('');
  const navigateTo = useNavigate();
  const { langStore } = useLangStore();
  const text = dataContext?.lang;
  const processedKeyRef = useRef(null);
  const [isHydrated, setIsHydrated] = useState(false);

  // Déduire la langue attendue via l'URL
  const urlLang = getLangFromPath(location.pathname);

  // Mark as hydrated after mount to avoid SSR hydration errors
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHydrated(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  //confirm Order
  const PostAPI = useCallback(() => {
    if (OrderKey.length > 20) {
      apiPostCall(ORDER_CONFIRM_URL + '/' + OrderKey, langStore, {})
        .then((response) => {
          startTransition(() => {
            setOrder(response?.data?.data);
          });

          // Auto-login avec le token reçu directement dans la réponse
          if (response?.data?.result?.status === 200 && response?.data?.data?.user?.token) {
            setUserToken(response.data.data.user);

            // Redirection vers la page de succès
            navigateTo('/' + location.pathname.split('/')[1] + '/order/success-h', {
              state: { success: true },
              replace: true,
            });
          } else {
            navigateTo('/' + location.pathname.split('/')[1] + '/order/success-h', {
              state: { success: true },
              replace: true,
            });
          }
        })

        .catch((err) => {
          ///belgique/order/error
          navigateTo('/' + location.pathname.split('/')[1] + '/order/error', {
            replace: true,
            state: {
              err: err.response?.data ?? {
                title: text?.form.unknownError,
              },
            },
          });
          if (!err?.response) {
            startTransition(() => {
              setErrMsg(text?.form.NoServerResponse);
            });
          } else {
            startTransition(() => {
              setErrMsg(err.response?.data?.title);
            });
          }
        });
    } else {
      //success-h
      navigateTo('/' + location.pathname.split('/')[1], {
        state: {},
        replace: true,
      });
    }
  }, [OrderKey, navigateTo, text, langStore, location.pathname]);

  useEffect(() => {
    // Wait for hydration to complete
    if (!isHydrated) {
      return;
    }

    // Si une langue est détectée dans l'URL mais qu'elle ne correspond pas encore au store,
    // on attend la synchronisation (gérée par App.jsx) avant de lancer l'appel.
    if (urlLang && langStore && urlLang !== langStore) {
      return;
    }

    if (!location.state) {
      if (OrderKey === 'error') {
        navigateTo('/' + location.pathname.split('/')[1], {
          replace: true,
        });
      } else {
        if (processedKeyRef.current !== OrderKey) {
          processedKeyRef.current = OrderKey;
          startTransition(() => {
            PostAPI();
          });
        }
      }
    } else if (location.state?.order) {
      startTransition(() => {
        setOrder(location.state?.order);
      });
      window.history.replaceState({}, '');
    } else if (location.state?.err) {
      startTransition(() => {
        setErrMsg(location.state?.err?.title);
      });
      window.history.replaceState({}, '');
    } else {
      if (location.state?.success) {
        window.history.replaceState({}, '');
      } else {
        startTransition(() => {
          setOrder(null);
          setPendingrMsg(location.state?.pending);
        });
      }
    }
  }, [isHydrated, OrderKey, PostAPI, location, navigateTo, urlLang, langStore]);

  return (
    <ContentWrapper>
      {!order?.rid ? (
        <>
          {errMsg && (
            <Infobox
              status='error'
              className={errMsg ? 'errmsg' : 'offscreen'}
              aria-live='assertive'>
              {errMsg}
            </Infobox>
          )}
          {pendingMsg && (
            <Infobox
              status='info'
              className={pendingMsg ? 'pendingmsg' : 'offscreen'}
              aria-live='assertive'>
              {pendingMsg}
            </Infobox>
          )}
        </>
      ) : (
        <Suspense fallback={null}><OrderSummary order={order} text={text} /></Suspense>
      )}
    </ContentWrapper>
  );
};

export default ConfirmOrder;

const ContentWrapper = styled.div.attrs({
  className: 'article ContentWrapper',
})`
  width: 100%;
  margin: 0 auto;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 0;
`;
